import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface AvatarVideoDimensions {
  width: number;
  height: number;
}

export interface AvatarVideoProps {
  /** Live avatar track. Takes precedence over static media. */
  stream?: MediaStream | null;
  /** Silent idle loop used before a live track is available. */
  src?: string;
  /** Still frame used for reduced-motion and poster-only presentations. */
  poster?: string;
  loop?: boolean;
  reducedMotion?: boolean;
  objectFit?: React.CSSProperties['objectFit'];
  className?: string;
  style?: React.CSSProperties;
  onError?: () => void;
  onReady?: () => void;
  /**
   * A non-looping static clip (`loop={false}`) played to its last frame — the
   * approach intro's one-shot clips hand over to the next source from here.
   * The element holds that last frame, so the cross-fade to whatever comes next
   * starts from the pose the clip ended on. Never fires for a live stream or a
   * looping clip.
   */
  onEnded?: () => void;
  /**
   * The static CLIP failed, whatever became of the poster. `onError` cannot
   * answer that: with a poster layered under the clip it fires only once BOTH
   * have failed, because a surface still showing its poster has not failed.
   * The approach intro needs the narrower question — a dead approach or far
   * loop must hand over at once, not sit on a healthy poster until a backstop
   * timer notices. Never fires for a live stream or a poster-only surface.
   */
  onVideoError?: () => void;
  /**
   * Stretch a one-shot static clip so it takes this long to play, by slowing
   * its playback (never speeding it up — see {@link stretchedPlaybackRate}).
   * The approach intro is a ~2.5s clip covering a connect that takes longer;
   * the clip's own length is read from its metadata, so a re-generated clip
   * of another length still lands on the same duration. Ignored for a live
   * stream. Absent → the clip plays at its own speed.
   */
  playForMs?: number;
  onDimensions?: (dims: AvatarVideoDimensions) => void;
  /**
   * Pixel-blur the foot of the keyed figure, inside the renderer. `span` is
   * the fraction of the frame's height the blur ramps over (from nothing at
   * its top to full at the bottom edge); `radius` is the full blur radius as
   * a fraction of the frame's height. Only the VIDEO pixels are blurred — a
   * `backdrop-filter` strip blurred the page behind the frame too, which
   * drew the strip's own rectangle on the host page. Absent → no blur.
   */
  edgeBlur?: AvatarEdgeBlur;
}

export interface AvatarEdgeBlur {
  /** Fraction of the frame's height the foot blur ramps over. */
  span: number;
  /** Full blur radius at the edge, as a fraction of the frame's height. */
  radius: number;
  /**
   * Fraction of the frame's width the side blur ramps over. The side and foot
   * ramps compound, so the bottom corners — where a cut-out torso meets the
   * frame on two edges at once — blur hardest.
   */
  sideSpan: number;
}

export interface GreenScreenAnalysis {
  confident: boolean;
  informativeRatio: number;
  greenRatio: number;
  borderGreenRatio: number;
}

export const AVATAR_SAMPLE_EDGE_PX = 24;
export const AVATAR_MAX_DEVICE_PIXEL_RATIO = 1.5;
export const AVATAR_MAX_RENDER_EDGE_PX = 1024;

/**
 * How far the surface has to move before a STATIC keyed source is redrawn at
 * the new size. A whole pixel: subpixel jitter from a reflow is not a resize,
 * and every presenter step this exists for (112 -> 160, 128 -> 160) is tens.
 */
export const RESIZE_REDRAW_EPSILON_PX = 1;

/** The live device pixel ratio, 1 where there is no window (SSR / node). */
function currentDevicePixelRatio(): number {
  if (typeof window === 'undefined') return 1;
  return window.devicePixelRatio || 1;
}

// FLO-745 measured budget (2026-08-28): Chrome 151 / Apple M3 Max Metal,
// supplied 960×960 H.264 at 24fps, 1024×1024 CSS surface at DPR 1.5 (the
// source-aware cap produced a 960×960 backing canvas), 120 rendered frames.
// JS submission: 0.318ms mean / 0.80ms p95; GPU timer: 0.132ms mean / 0.157ms
// p95; playback: 0 dropped of 119 decoded frames. This is the largest backing
// surface the supplied source can drive; smaller launcher/chat/PiP surfaces
// scale below it via avatarCanvasSize(). The acceptance budget is p95 JS
// submission <= 1ms/frame, GPU timer <= 2ms/frame, and dropped-frame ratio < 1%
// at the capped largest surface. This M3 Max result is the available baseline;
// mobile/cross-device confirmation remains part of manual QA.

const MAX_PROBE_ATTEMPTS = 8;
const PROBE_RETRY_MS = 180;
const MAX_LIVE_DRAW_RETRIES = 8;
const LIVE_DRAW_INITIAL_RETRY_MS = 50;
const LIVE_DRAW_RETRY_BACKOFF_MS = 500;

/**
 * The slowest a stretched clip may play. The intro clips are 25fps, so this is
 * 15 frames a second: below it the slow-down itself becomes the thing the
 * visitor notices.
 */
export const MIN_STRETCH_PLAYBACK_RATE = 0.6;

/**
 * The playback rate that makes a clip `durationSec` long take `playForMs`.
 *
 * 1 whenever there is nothing to stretch: no target, a duration the element
 * has not reported yet (NaN before metadata, Infinity for a stream), or a clip
 * already at least as long as the target — a clip is never sped up, because a
 * hurried presenter reads worse than a short wait.
 */
export const stretchedPlaybackRate = (durationSec: number, playForMs: number | undefined): number => {
  if (!playForMs || !(playForMs > 0) || !Number.isFinite(durationSec) || !(durationSec > 0)) {
    return 1;
  }
  return Math.min(1, Math.max(MIN_STRETCH_PLAYBACK_RATE, (durationSec * 1000) / playForMs));
};

/**
 * Cross-fade duration between two avatar sources (idle loop <-> live stream).
 *
 * Both sources are chroma-keyed cut-out figures composited over the host page,
 * so the dissolve runs over transparency rather than between two opaque
 * rectangles — which is why a short fade reads as continuous motion instead of
 * a wipe.
 */
export const AVATAR_CROSSFADE_MS = 220;

/**
 * Longest the outgoing source is held while waiting for the incoming one to
 * paint.
 *
 * Anam exposes no way to choose the frame a session starts on — there is no
 * start-frame, seek, offset or pose argument anywhere in its API (verified
 * against all 37 paths of api.anam.ai/swagger.json, `SessionOptions` and
 * `PersonaConfig`). So the only lever for a seamless handover is *when* we
 * reveal the new surface: hold the old one until `onReady` reports the new one
 * has actually painted its first frame. This cap is the backstop for a source
 * that never paints (dead URL, lost GL context) — without it a failed live
 * track would strand the idle loop on screen for the rest of the session.
 */
export const AVATAR_CROSSFADE_MAX_HOLD_MS = 4000;

/**
 * How many times a surface may swap in a fresh `<canvas>` to escape a lost GPU
 * context before giving up. Bounded so a genuinely broken GPU can't spin.
 */
const MAX_CANVAS_RECOVERIES = 2;

/**
 * A bounded, one-shot classifier for deciding whether a static source is
 * actually a green-screen asset. Live Anam avatar tracks are known-green and
 * enter the compositor directly, while ordinary static videos remain untouched.
 * The hot render path stays on the GPU; this samples only 24×24 pixels while a
 * static source becomes ready, avoiding a per-frame getImageData loop.
 */
export function analyzeGreenScreen(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
): GreenScreenAnalysis {
  if (width <= 0 || height <= 0 || pixels.length < width * height * 4) {
    return { confident: false, informativeRatio: 0, greenRatio: 0, borderGreenRatio: 0 };
  }

  let informative = 0;
  let green = 0;
  let border = 0;
  let borderGreen = 0;
  const borderDepth = Math.max(1, Math.floor(Math.min(width, height) / 6));

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      const r = pixels[i] / 255;
      const g = pixels[i + 1] / 255;
      const b = pixels[i + 2] / 255;
      const a = pixels[i + 3] / 255;
      // Top and side edges only. A head-and-shoulders portrait fills its
      // bottom edge with the body — the recorded 768x1152 loops have torso
      // across the whole bottom row — so counting it left the catalog's own
      // clip 0.005 above the confidence floor, one resampling filter away
      // from rendering raw. The backdrop is what surrounds the figure, and
      // that is the top and the sides.
      const isBorder = x < borderDepth || x >= width - borderDepth || y < borderDepth;
      const visible = a > 0.2 && Math.max(r, g, b) > 0.08;
      const greenDominance = g - Math.max(r, b);
      // Tuned against the supplied #04cb18 / #0add15 media while remaining
      // deliberately stricter than "contains green". A lawn, chart, or green
      // shirt does not fill the outer frame with this degree of dominance.
      const isGreen = visible && g > 0.32 && greenDominance > 0.16 && g > r * 1.24 && g > b * 1.18;

      if (visible) informative += 1;
      if (isGreen) green += 1;
      if (isBorder) {
        border += 1;
        if (isGreen) borderGreen += 1;
      }
    }
  }

  const total = width * height;
  const informativeRatio = informative / total;
  const greenRatio = green / total;
  const borderGreenRatio = border ? borderGreen / border : 0;
  return {
    confident:
      informativeRatio >= 0.35 && greenRatio >= 0.2 && borderGreenRatio >= 0.52,
    informativeRatio,
    greenRatio,
    borderGreenRatio,
  };
}

/** Visible-size-aware backing resolution, capped for a bounded GPU upload. */
export function avatarCanvasSize(
  sourceWidth: number,
  sourceHeight: number,
  displayWidth: number,
  displayHeight: number,
  devicePixelRatio = 1,
): AvatarVideoDimensions {
  const safeSourceWidth = Number.isFinite(sourceWidth) && sourceWidth > 0 ? sourceWidth : 16;
  const safeSourceHeight = Number.isFinite(sourceHeight) && sourceHeight > 0 ? sourceHeight : 9;
  const sourceLongEdge = Math.max(safeSourceWidth, safeSourceHeight);
  const displayLongEdge = Math.max(
    Number.isFinite(displayWidth) ? displayWidth : 0,
    Number.isFinite(displayHeight) ? displayHeight : 0,
  );
  const safeDpr = Math.min(
    AVATAR_MAX_DEVICE_PIXEL_RATIO,
    Math.max(1, Number.isFinite(devicePixelRatio) ? devicePixelRatio : 1),
  );
  const desiredLongEdge = displayLongEdge > 0
    ? displayLongEdge * safeDpr
    : Math.min(sourceLongEdge, 640);
  const targetLongEdge = Math.max(1, Math.min(sourceLongEdge, desiredLongEdge, AVATAR_MAX_RENDER_EDGE_PX));
  const scale = targetLongEdge / sourceLongEdge;
  return {
    width: Math.max(1, Math.round(safeSourceWidth * scale)),
    height: Math.max(1, Math.round(safeSourceHeight * scale)),
  };
}

type MediaElement = HTMLVideoElement | HTMLImageElement;
type RenderMode = 'pending' | 'native' | 'keyed';

interface WebGlResources {
  gl: WebGLRenderingContext;
  program: WebGLProgram;
  buffer: WebGLBuffer;
  texture: WebGLTexture;
  positionLocation: number;
  texelLocation: WebGLUniformLocation | null;
  edgeBlurLocation: WebGLUniformLocation | null;
}

const VERTEX_SHADER = `
  attribute vec2 a_position;
  varying vec2 v_uv;
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_uv = a_position * 0.5 + 0.5;
  }
`;

// The alpha edge is intentionally soft: it removes the supplied saturated
// green while de-spilling hair and shoulders instead of cutting a hard halo.
const FRAGMENT_SHADER = `
  precision mediump float;
  uniform sampler2D u_image;
  uniform vec2 u_texel;
  // x: span of the foot blur (fraction of height); y: its full radius
  // (fraction of height); z: span of the side blur (fraction of width).
  // All zero disables it and costs one branch per pixel.
  uniform vec3 u_edgeBlur;
  varying vec2 v_uv;

  float greenKey(vec3 color) {
    float dominance = color.g - max(color.r, color.b);
    return smoothstep(0.07, 0.27, dominance) * smoothstep(0.25, 0.48, color.g);
  }

  vec4 spillSuppressed(vec4 pixel, float key, float edge) {
    float dominance = pixel.g - max(pixel.r, pixel.b);
    float alpha = pixel.a * (1.0 - key);
    float spill = max(smoothstep(0.025, 0.20, dominance), edge);
    float balancedGreen = min(pixel.g, (pixel.r + pixel.b) * 0.5);
    vec3 color = mix(pixel.rgb, vec3(pixel.r, balancedGreen, pixel.b), spill);
    return vec4(color * alpha, alpha);
  }

  // The full key: the pixel plus eight neighbours, feathered. Removes the
  // codec-blended green rim around hair and shoulders.
  vec4 keyed(vec2 uv) {
    vec4 pixel = texture2D(u_image, uv);
    float key = greenKey(pixel.rgb);
    vec2 edgeTexel = u_texel * 4.0;
    float cardinalKey = max(
      max(greenKey(texture2D(u_image, uv + vec2(edgeTexel.x, 0.0)).rgb),
          greenKey(texture2D(u_image, uv - vec2(edgeTexel.x, 0.0)).rgb)),
      max(greenKey(texture2D(u_image, uv + vec2(0.0, edgeTexel.y)).rgb),
          greenKey(texture2D(u_image, uv - vec2(0.0, edgeTexel.y)).rgb))
    );
    float diagonalKey = max(
      max(greenKey(texture2D(u_image, uv + edgeTexel).rgb),
          greenKey(texture2D(u_image, uv - edgeTexel).rgb)),
      max(greenKey(texture2D(u_image, uv + vec2(edgeTexel.x, -edgeTexel.y)).rgb),
          greenKey(texture2D(u_image, uv + vec2(-edgeTexel.x, edgeTexel.y)).rgb))
    );
    float edge = smoothstep(0.42, 0.92, max(cardinalKey, diagonalKey));
    key = max(key, edge * 0.65);
    return spillSuppressed(pixel, key, edge);
  }

  // One-read key for blur taps: the average over the kernel does the
  // feathering that the neighbour search does for a sharp pixel.
  vec4 keyedLite(vec2 uv) {
    vec4 pixel = texture2D(u_image, uv);
    return spillSuppressed(pixel, greenKey(pixel.rgb), 0.0);
  }

  void main() {
    // 1 at the bottom edge, 0 at the top of the span. Premultiplied output
    // throughout, so the blurred figure's edge dissolves to transparent
    // rather than to a dark fringe.
    float foot = u_edgeBlur.x > 0.0 ? 1.0 - smoothstep(0.0, u_edgeBlur.x, v_uv.y) : 0.0;
    float side = u_edgeBlur.z > 0.0
      ? 1.0 - smoothstep(0.0, u_edgeBlur.z, min(v_uv.x, 1.0 - v_uv.x))
      : 0.0;
    // Compound rather than max: a corner is both a foot and a side.
    float strength = 1.0 - (1.0 - foot) * (1.0 - side);
    vec4 sharp = keyed(v_uv);
    if (strength <= 0.001) {
      gl_FragColor = sharp;
      return;
    }
    // Isotropic in pixels: the horizontal step is the vertical one scaled by
    // the frame's aspect (u_texel.x / u_texel.y == height / width).
    float r = u_edgeBlur.y * strength * 0.5;
    vec2 stepUv = vec2(r * u_texel.x / u_texel.y, r);
    // 5x5 binomial kernel (1 4 6 4 1)^2 / 256.
    float w[5];
    w[0] = 1.0; w[1] = 4.0; w[2] = 6.0; w[3] = 4.0; w[4] = 1.0;
    vec4 sum = vec4(0.0);
    for (int y = 0; y < 5; y++) {
      for (int x = 0; x < 5; x++) {
        vec2 offset = vec2(float(x - 2), float(y - 2)) * stepUv;
        sum += keyedLite(v_uv + offset) * (w[x] * w[y]);
      }
    }
    vec4 blurred = sum / 256.0;
    gl_FragColor = mix(sharp, blurred, smoothstep(0.0, 0.3, strength));
  }
`;

function compileShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string,
): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function createWebGlResources(canvas: HTMLCanvasElement): WebGlResources | null {
  const gl = canvas.getContext('webgl', {
    alpha: true,
    premultipliedAlpha: true,
    antialias: false,
    depth: false,
    stencil: false,
    preserveDrawingBuffer: false,
  });
  if (!gl) return null;

  const vertex = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
  if (!vertex || !fragment) {
    if (vertex) gl.deleteShader(vertex);
    if (fragment) gl.deleteShader(fragment);
    return null;
  }

  const program = gl.createProgram();
  if (!program) {
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    return null;
  }
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }

  const buffer = gl.createBuffer();
  const texture = gl.createTexture();
  const positionLocation = gl.getAttribLocation(program, 'a_position');
  const texelLocation = gl.getUniformLocation(program, 'u_texel');
  const edgeBlurLocation = gl.getUniformLocation(program, 'u_edgeBlur');
  if (!buffer || !texture || positionLocation < 0) {
    if (buffer) gl.deleteBuffer(buffer);
    if (texture) gl.deleteTexture(texture);
    gl.deleteProgram(program);
    return null;
  }

  gl.useProgram(program);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
    gl.STATIC_DRAW,
  );
  gl.enableVertexAttribArray(positionLocation);
  gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
  gl.uniform1i(gl.getUniformLocation(program, 'u_image'), 0);
  if (edgeBlurLocation !== null) gl.uniform3f(edgeBlurLocation, 0, 0, 0);

  return { gl, program, buffer, texture, positionLocation, texelLocation, edgeBlurLocation };
}

function mediaDimensions(media: MediaElement): AvatarVideoDimensions {
  if (media instanceof HTMLVideoElement) {
    return { width: media.videoWidth, height: media.videoHeight };
  }
  return { width: media.naturalWidth, height: media.naturalHeight };
}

/** Query parameter that gives the SDK's CORS loads their own cache key. */
export const AVATAR_CORS_CACHE_KEY_PARAM = 'cors=1';

/**
 * The URL a static catalog asset is loaded from under CORS.
 *
 * Chrome keys its HTTP cache by URL alone — not by request mode — and honours
 * `Vary` only when the cached copy carries one. The asset host answers a
 * request that sends no `Origin` header with neither
 * `Access-Control-Allow-Origin` nor `Vary`, so ONE plain load of the URL by
 * anything (the dashboard picker's preview — the cache is partitioned by
 * SITE, so dashboard.floe.so and floe.so share it — a `poster` attribute, an
 * older SDK build's no-CORS fallback) leaves a copy that every later
 * `crossOrigin="anonymous"` load of that URL is handed and fails the CORS
 * check on. The assets are `immutable` for a year, so nothing revalidates it.
 *
 * Loading a query-string variant nothing else uses gives the SDK a cache key
 * no non-CORS loader can poison: on every browser, with no extra request, no
 * repair step and no CSP `connect-src` requirement on the customer's page.
 * R2 ignores the query and the CDN keys on it, so the variant is served and
 * cached exactly like the plain URL. Nothing outside this component may load
 * the variant without `crossOrigin` — that would recreate the poison.
 *
 * `blob:` and `data:` URLs are not HTTP-cached and must not be edited.
 */
export function corsCacheKeyUrl(url: string): string {
  if (/^(blob|data|file):/i.test(url)) return url;
  const hashIndex = url.indexOf('#');
  const base = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  if (/[?&]cors=1(?:&|$)/.test(base)) return url;
  return `${base}${base.includes('?') ? '&' : '?'}${AVATAR_CORS_CACHE_KEY_PARAM}${hash}`;
}

/**
 * Shared avatar renderer for launcher, chat, rail and PiP surfaces.
 *
 * It follows Anam's supported hidden-video + WebGL-canvas architecture. The
 * native media stays mounted as the presentation for a static source the
 * classifier finds NOT to be green-screen. It is never the answer to a load
 * failure: static media is always loaded under CORS, on the SDK's own cache
 * key ({@link corsCacheKeyUrl}), and a failure is reported to the caller,
 * which degrades to the poster or the pill. The only static media this
 * renders is the catalog's green-screen loop and poster, so a `<video>`
 * remounted without `crossOrigin` would paint the raw green backdrop on the
 * customer's page — and write the no-CORS copy that poisons the cache. Once a
 * source is known-green, compositor failure is kept transparent so the raw
 * green frame can never flash through.
 */
const AvatarVideoSurface: React.FC<AvatarVideoProps> = ({
  stream,
  src,
  poster,
  loop = true,
  reducedMotion = false,
  objectFit = 'contain',
  className,
  style,
  onError,
  onReady,
  onEnded,
  onVideoError,
  playForMs,
  onDimensions,
  edgeBlur,
}) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  // Read by the draw loop each frame; a ref so a changed prop never has to
  // tear down the WebGL context to take effect.
  const edgeBlurRef = useRef<AvatarEdgeBlur | undefined>(edgeBlur);
  edgeBlurRef.current = edgeBlur;
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const displaySizeRef = useRef({ width: 0, height: 0 });
  // The CSS box and device pixel ratio the canvas was last PAINTED at. The
  // resize path compares against this rather than against the previous
  // observation, so a slow drag of sub-pixel steps that sums to tens of pixels
  // still repaints — comparing consecutive observations, every step is noise.
  const drawnSizeRef = useRef({ width: 0, height: 0, dpr: 0 });
  // Installed by the render effect: resize the backing store and paint one
  // frame from the CURRENT source, with the GL resources already built.
  const redrawRef = useRef<(() => void) | null>(null);
  const probeTimerRef = useRef<number | null>(null);
  const probeAttemptsRef = useRef(0);
  const probeRunningRef = useRef(false);
  const probeSettledRef = useRef(false);
  const rendererWaitingForDimensionsRef = useRef(false);
  // Set by the render effect's cleanup; called only when the surface unmounts.
  const loseContextRef = useRef<(() => void) | null>(null);
  // Identity of the <canvas> element. A canvas returns the SAME context object
  // from getContext('webgl') for its whole life, so a context that has been
  // lost can never compile a shader again — the only way back is a new element.
  // Bumping this is how the surface escapes a lost context instead of hiding
  // the avatar for the rest of the session.
  const [canvasEpoch, setCanvasEpoch] = useState(0);
  const canvasRecoveriesRef = useRef(0);
  const readyNotifiedRef = useRef(false);
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const onEndedRef = useRef(onEnded);
  const onVideoErrorRef = useRef(onVideoError);
  onReadyRef.current = onReady;
  onErrorRef.current = onError;
  onEndedRef.current = onEnded;
  onVideoErrorRef.current = onVideoError;
  const handleEnded = useCallback(() => onEndedRef.current?.(), []);

  const useStaticImage = !!poster && (reducedMotion || (!stream && !src));
  const isLiveStream = !!stream && !useStaticImage;
  const sourceKey = (useStaticImage ? poster : stream || src) || null;
  const [mode, setMode] = useState<RenderMode>(() => (isLiveStream ? 'keyed' : 'pending'));
  const [canvasReady, setCanvasReady] = useState(false);
  const [renderEpoch, setRenderEpoch] = useState(0);

  useEffect(() => {
    // The Anam MediaStream is a known-green product track. Arm its compositor
    // from the source transition itself instead of depending on a loadeddata or
    // canplay edge that an already-flowing WebRTC stream may not emit again.
    // `canvasReady` stays false until the first successful GPU upload, so the
    // layered poster remains visible throughout warm-up.
    setMode(isLiveStream ? 'keyed' : 'pending');
    setCanvasReady(false);
    probeAttemptsRef.current = 0;
    probeRunningRef.current = false;
    probeSettledRef.current = isLiveStream;
    rendererWaitingForDimensionsRef.current = false;
    canvasRecoveriesRef.current = 0; // a new source gets a fresh recovery budget
    readyNotifiedRef.current = false;
    if (probeTimerRef.current !== null) window.clearTimeout(probeTimerRef.current);
    return () => {
      if (probeTimerRef.current !== null) window.clearTimeout(probeTimerRef.current);
    };
  }, [isLiveStream, sourceKey]);

  // A resize has to REDRAW, not merely be recorded.
  //
  // The presenter is no longer a fixed box (112x132 / 128x192 / 160x240, picked
  // from the viewport), and a source that is painted ONCE keeps whatever
  // backing store it was born with: recording the new size and stopping there
  // left the face upscaled and soft for the rest of the call, with no later
  // event that would ever correct it. "Painted once" is not only the <img>
  // path — a paused idle loop, or a video whose autoplay was refused, is just
  // as static, and `draw()` samples whatever frame the element is showing.
  //
  // This repaints DIRECTLY — no React state. Bumping a render epoch instead
  // re-ran the whole render effect, which tears down and rebuilds the GL
  // resources (context, two shader compiles, a link, buffers, a texture
  // upload). On the layered surface the poster canvas stays mounted at opacity
  // 0 under a live stream, so that cost was being paid on an INVISIBLE canvas —
  // and every rebuild that failed spent one of MAX_CANVAS_RECOVERIES, a budget
  // that only resets on a source change. Two keyboard-driven resizes could
  // therefore hide the presenter for the rest of the session. A direct draw
  // costs one draw call, which is cheap enough that the hidden layer needs no
  // dirty-tracking of its own.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    // `null`, not 0: a shimmed requestAnimationFrame may hand back 0 as a valid
    // handle, and `if (handle)` would then treat a scheduled frame as idle.
    let redrawFrame: number | null = null;
    const measure = () => {
      // The LAYOUT box, not the transformed one: getBoundingClientRect reports
      // the dock's pop-in `scale(0.96)` for its first frames, and a canvas
      // sized from that stays 4% short for good once the layout box, which the
      // observer watches, never changes again.
      displaySizeRef.current = { width: wrapper.offsetWidth, height: wrapper.offsetHeight };
    };
    const staleAgainstPainted = () => {
      const drawn = drawnSizeRef.current;
      return (
        Math.abs(displaySizeRef.current.width - drawn.width) >= RESIZE_REDRAW_EPSILON_PX
        || Math.abs(displaySizeRef.current.height - drawn.height) >= RESIZE_REDRAW_EPSILON_PX
        || currentDevicePixelRatio() !== drawn.dpr
      );
    };
    const cancelRedraw = () => {
      if (redrawFrame !== null) cancelAnimationFrame(redrawFrame);
      redrawFrame = null;
    };
    const scheduleRedraw = () => {
      if (redrawFrame !== null) return;
      // One frame of debounce: a resize DRAG fires the observer continuously.
      redrawFrame = requestAnimationFrame(() => {
        redrawFrame = null;
        // Re-measure inside the frame: a box that bounced out and back is a
        // no-op, and the last measurement of a drag is the one to paint at.
        measure();
        if (staleAgainstPainted()) redrawRef.current?.();
      });
    };
    const update = () => {
      measure();
      // Nothing has painted yet — the first draw reads displaySizeRef itself.
      if (!redrawRef.current) return;
      if (staleAgainstPainted()) scheduleRedraw();
    };
    update();
    // Both branches also listen to the window: a page zoom moves the device
    // pixel ratio without moving a fixed-px box, so the observer stays silent,
    // but zoom fires `resize` — and the staleness test above compares the
    // ratio too. That repairs the backing store on a browser whose
    // `(resolution)` query (below) is absent or never fires.
    window.addEventListener('resize', update);
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        window.removeEventListener('resize', update);
        cancelRedraw();
      };
    }
    const observer = new ResizeObserver(update);
    observer.observe(wrapper);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      cancelRedraw();
    };
  }, []);

  // The other way a once-painted canvas goes stale: the device pixel ratio
  // changes with no box change at all — page zoom, or a window dragged between
  // a Retina and a non-Retina display. There is no event for it, but a
  // `(resolution: Ndppx)` query stops matching the moment it moves, so each
  // change re-arms on the new ratio and repaints through the same path.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    let stop: (() => void) | null = null;
    const arm = () => {
      stop?.();
      stop = null;
      try {
        const mql = window.matchMedia(`(resolution: ${currentDevicePixelRatio()}dppx)`);
        const onChange = () => {
          arm();
          redrawRef.current?.();
        };
        if (typeof mql.addEventListener === 'function') {
          mql.addEventListener('change', onChange);
          stop = () => mql.removeEventListener('change', onChange);
        } else if (
          typeof mql.addListener === 'function'
          && typeof mql.removeListener === 'function'
        ) {
          mql.addListener(onChange);
          stop = () => mql.removeListener(onChange);
        }
      } catch {
        // A host shim that cannot parse the query: no DPR tracking, and the
        // ResizeObserver above still covers every change that moves the box.
      }
    };
    arm();
    return () => stop?.();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isLiveStream) video.srcObject = stream;
    else video.srcObject = null;
    // Static media has the autoplay attribute and lets the browser own that
    // lifecycle. A dynamically attached MediaStream needs an explicit play.
    if (isLiveStream) {
      // Older WebViews (and jsdom) may expose play() without returning the
      // standards-era Promise. Autoplay failure is non-fatal because the
      // native poster/fallback remains visible.
      const playResult = video.play();
      playResult?.catch?.(() => {});
    }
    return () => {
      // Detach only our element. The SDK owns the MediaStream and its tracks.
      if (video.srcObject === stream) video.srcObject = null;
    };
  }, [isLiveStream, src, stream]);

  const currentMedia = useCallback((): MediaElement | null => {
    return useStaticImage ? imageRef.current : videoRef.current;
  }, [useStaticImage]);

  const reportDimensions = useCallback(() => {
    const media = currentMedia();
    if (!media) return;
    const dims = mediaDimensions(media);
    if (dims.width > 0 && dims.height > 0) onDimensions?.(dims);
  }, [currentMedia, onDimensions]);

  // A stretched one-shot clip (`playForMs`). Its length is only known once
  // metadata has loaded, and a load resets the element's rate, so this runs
  // from `loadedmetadata` (every new source) as well as when the target changes.
  const applyPlaybackRate = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const rate = isLiveStream ? 1 : stretchedPlaybackRate(video.duration, playForMs);
    try {
      if (video.playbackRate !== rate) {
        video.playbackRate = rate;
      }
    } catch {
      // A host that refuses the rate plays the clip at its own speed.
    }
  }, [isLiveStream, playForMs]);
  useEffect(() => {
    applyPlaybackRate();
  }, [applyPlaybackRate]);
  const handleLoadedMetadata = useCallback(() => {
    reportDimensions();
    applyPlaybackRate();
  }, [reportDimensions, applyPlaybackRate]);

  const notifyReady = useCallback(() => {
    if (readyNotifiedRef.current) return;
    readyNotifiedRef.current = true;
    onReadyRef.current?.();
  }, []);

  /**
   * Swap in a fresh `<canvas>`, which is the only way to get a live context
   * once the current one is lost. Returns false when the budget is spent, so
   * the caller falls through to the ordinary failure path.
   */
  const recoverCanvas = useCallback(() => {
    if (canvasRecoveriesRef.current >= MAX_CANVAS_RECOVERIES) return false;
    canvasRecoveriesRef.current += 1;
    setCanvasEpoch((epoch) => epoch + 1);
    return true;
  }, []);

  const failKnownGreenRender = useCallback(() => {
    // Once classification confirms a chroma source, a renderer failure must
    // never reveal that source as a native green rectangle. Layered callers
    // keep/reveal their keyed poster; unlayered callers stay transparent and
    // receive the ordinary media-failure signal so they can degrade safely.
    setCanvasReady(false);
    setMode('pending');
    onErrorRef.current?.();
  }, []);

  const probe = useCallback(() => {
    const media = currentMedia();
    if (!media) {
      probeRunningRef.current = false;
      return;
    }
    const dims = mediaDimensions(media);
    if (!dims.width || !dims.height) {
      probeRunningRef.current = false;
      return;
    }
    probeAttemptsRef.current += 1;

    try {
      const sample = document.createElement('canvas');
      sample.width = AVATAR_SAMPLE_EDGE_PX;
      sample.height = AVATAR_SAMPLE_EDGE_PX;
      const ctx = sample.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        probeRunningRef.current = false;
        probeSettledRef.current = true;
        setMode('native');
        notifyReady();
        return;
      }
      ctx.drawImage(media, 0, 0, AVATAR_SAMPLE_EDGE_PX, AVATAR_SAMPLE_EDGE_PX);
      const analysis = analyzeGreenScreen(
        ctx.getImageData(0, 0, AVATAR_SAMPLE_EDGE_PX, AVATAR_SAMPLE_EDGE_PX).data,
        AVATAR_SAMPLE_EDGE_PX,
        AVATAR_SAMPLE_EDGE_PX,
      );
      if (analysis.confident) {
        probeRunningRef.current = false;
        probeSettledRef.current = true;
        setMode('keyed');
        return;
      }
      // A black/empty startup frame is not evidence that static media lacks a
      // green background. Retry briefly; an informative ordinary asset bypasses
      // immediately and does not pay that delay.
      if (
        analysis.informativeRatio < 0.35 &&
        probeAttemptsRef.current < MAX_PROBE_ATTEMPTS
      ) {
        probeTimerRef.current = window.setTimeout(probe, PROBE_RETRY_MS);
        return;
      }
      probeRunningRef.current = false;
      probeSettledRef.current = true;
      setMode('native');
      notifyReady();
    } catch {
      // Canvas security errors (missing CORS headers) and decoder quirks fall
      // back to the ordinary media element instead of blanking the avatar.
      probeRunningRef.current = false;
      probeSettledRef.current = true;
      setMode('native');
      notifyReady();
    }
  }, [currentMedia, notifyReady]);

  const handleMediaReady = useCallback(() => {
    reportDimensions();
    if (isLiveStream) {
      // Anam's live avatar product track is a deliberate green-screen feed.
      // Enter WebGL immediately: any non-green provider slate passes through
      // unchanged, and a late avatar frame can never become a raw green box.
      probeRunningRef.current = false;
      probeSettledRef.current = true;
      setMode('keyed');
      // If the initial keyed effect ran before video dimensions existed, wake
      // it immediately now rather than waiting for a separate frame callback.
      if (rendererWaitingForDimensionsRef.current) {
        rendererWaitingForDimensionsRef.current = false;
        setRenderEpoch((epoch) => epoch + 1);
      }
      return;
    }
    // loadedData and canPlay frequently fire back-to-back. One classifier (and
    // at most one retry timer) owns a source at a time.
    if (probeRunningRef.current || probeSettledRef.current) return;
    probeRunningRef.current = true;
    probe();
  }, [isLiveStream, notifyReady, probe, reportDimensions]);

  const handleMediaError = useCallback(() => {
    // Static media is loaded under CORS on the SDK's own cache key (see
    // corsCacheKeyUrl), so a failure here is a real one — the host refused
    // CORS, the URL is dead, the network is down — and the caller degrades to
    // the poster or the pill. It used to remount the element WITHOUT
    // crossOrigin instead: that painted the raw green backdrop on the
    // customer's page, and the no-CORS load it made was itself what poisoned
    // the cache for every later CORS load of the same URL.
    if (probeTimerRef.current !== null) window.clearTimeout(probeTimerRef.current);
    probeRunningRef.current = false;
    probeSettledRef.current = true;
    if (!useStaticImage && !isLiveStream) onVideoErrorRef.current?.();
    onErrorRef.current?.();
  }, [isLiveStream, useStaticImage]);

  useEffect(() => {
    if (mode !== 'keyed') return;
    const canvas = canvasRef.current;
    const media = currentMedia();
    if (!canvas || !media) return;
    const video = media instanceof HTMLVideoElement ? media : null;
    const initialDimensions = mediaDimensions(media);
    if (!initialDimensions.width || !initialDimensions.height) {
      rendererWaitingForDimensionsRef.current = true;
      let stopped = false;
      let raf = 0;
      let frameCallback = 0;
      const waitForDimensions = () => {
        if (stopped) return;
        const dimensions = mediaDimensions(media);
        if (dimensions.width && dimensions.height) {
          rendererWaitingForDimensionsRef.current = false;
          setRenderEpoch((epoch) => epoch + 1);
          return;
        }
        if (video && typeof video.requestVideoFrameCallback === 'function') {
          frameCallback = video.requestVideoFrameCallback(waitForDimensions);
        } else {
          raf = requestAnimationFrame(waitForDimensions);
        }
      };
      waitForDimensions();
      return () => {
        stopped = true;
        if (raf) cancelAnimationFrame(raf);
        if (video && frameCallback && typeof video.cancelVideoFrameCallback === 'function') {
          video.cancelVideoFrameCallback(frameCallback);
        }
      };
    }
    rendererWaitingForDimensionsRef.current = false;
    const resources = createWebGlResources(canvas);
    if (!resources) {
      // Most often this canvas is holding a context something already lost —
      // our own unmount disposer under a StrictMode/Offscreen remount, or a
      // real GPU reset. Take a fresh canvas before concluding the source is
      // unrenderable; hiding both layers is permanent, and a blank presenter is
      // exactly the failure this component was fixed for.
      if (recoverCanvas()) return;
      failKnownGreenRender();
      return;
    }

    const { gl, program, buffer, texture, texelLocation, edgeBlurLocation } = resources;
    let stopped = false;
    let raf = 0;
    let frameCallback = 0;
    let painted = false;
    let failed = false;
    let consecutiveDrawFailures = 0;
    let liveDrawRetryTimer: number | null = null;

    const failRender = () => {
      if (failed || stopped) return;
      failed = true;
      stopped = true;
      failKnownGreenRender();
    };

    const draw = () => {
      if (stopped) return;
      if (
        isLiveStream &&
        liveDrawRetryTimer !== null &&
        consecutiveDrawFailures > MAX_LIVE_DRAW_RETRIES
      ) {
        return;
      }
      const dims = mediaDimensions(media);
      if (!dims.width || !dims.height) return;
      const dpr = currentDevicePixelRatio();
      const target = avatarCanvasSize(
        dims.width,
        dims.height,
        displaySizeRef.current.width,
        displaySizeRef.current.height,
        dpr,
      );
      if (canvas.width !== target.width || canvas.height !== target.height) {
        canvas.width = target.width;
        canvas.height = target.height;
      }
      try {
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.useProgram(program);
        if (texelLocation !== null) {
          gl.uniform2f(texelLocation, 1 / dims.width, 1 / dims.height);
        }
        if (edgeBlurLocation !== null) {
          const blur = edgeBlurRef.current;
          gl.uniform3f(edgeBlurLocation, blur?.span ?? 0, blur?.radius ?? 0, blur?.sideSpan ?? 0);
        }
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, media);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        if (liveDrawRetryTimer !== null) {
          window.clearTimeout(liveDrawRetryTimer);
          liveDrawRetryTimer = null;
        }
        consecutiveDrawFailures = 0;
        // Recorded on the success path by construction. Nothing observable
        // rests on that today — a throwing draw is terminal for a once-painted
        // source (failRender degrades the surface) and retried every frame for
        // a live one — but "what we painted" should never be set by a paint
        // that did not happen.
        drawnSizeRef.current = {
          width: displaySizeRef.current.width,
          height: displaySizeRef.current.height,
          dpr,
        };
        if (!painted) {
          painted = true;
          setCanvasReady(true);
          notifyReady();
        }
      } catch {
        consecutiveDrawFailures += 1;
        // A WebRTC video can report dimensions just before its first frame is
        // uploadable. Keep recovery independent of receiving another frame so
        // a paused warm-up frame can become visible without a UI remount. Frame
        // callbacks provide the initial burst; the timer backs off GPU uploads
        // while the poster remains visible.
        if (isLiveStream) {
          if (
            liveDrawRetryTimer !== null &&
            consecutiveDrawFailures === MAX_LIVE_DRAW_RETRIES + 1
          ) {
            window.clearTimeout(liveDrawRetryTimer);
            liveDrawRetryTimer = null;
          }
          if (liveDrawRetryTimer === null) {
            const retryDelay =
              consecutiveDrawFailures > MAX_LIVE_DRAW_RETRIES
                ? LIVE_DRAW_RETRY_BACKOFF_MS
                : LIVE_DRAW_INITIAL_RETRY_MS;
            liveDrawRetryTimer = window.setTimeout(() => {
              liveDrawRetryTimer = null;
              draw();
            }, retryDelay);
          }
          return;
        }
        failRender();
      }
    };

    const scheduleVideoFrame = () => {
      if (!video || stopped) return;
      if (typeof video.requestVideoFrameCallback === 'function') {
        frameCallback = video.requestVideoFrameCallback(() => {
          draw();
          scheduleVideoFrame();
        });
      } else {
        raf = requestAnimationFrame(() => {
          draw();
          scheduleVideoFrame();
        });
      }
    };

    const contextLost = (event: Event) => {
      // preventDefault() is what makes the loss recoverable at all. A driver
      // reset or a backgrounded tab must not cost the visitor their presenter.
      event.preventDefault();
      // Order matters: failRender() bails on `stopped`, so setting it up front
      // would swallow the degrade path once the recovery budget is spent and
      // leave the surface keyed, invisible, and silent.
      if (recoverCanvas()) {
        stopped = true;
        return;
      }
      failRender();
    };
    canvas.addEventListener('webglcontextlost', contextLost);
    // The resize path's entry point, with these GL resources already built:
    // `draw()` re-reads displaySizeRef, resizes the backing store through
    // avatarCanvasSize and paints one frame. A frame loop, where there is one,
    // simply gets there first.
    redrawRef.current = draw;
    draw();
    scheduleVideoFrame();

    return () => {
      stopped = true;
      redrawRef.current = null;
      if (raf) cancelAnimationFrame(raf);
      if (video && frameCallback && typeof video.cancelVideoFrameCallback === 'function') {
        video.cancelVideoFrameCallback(frameCallback);
      }
      if (liveDrawRetryTimer !== null) window.clearTimeout(liveDrawRetryTimer);
      canvas.removeEventListener('webglcontextlost', contextLost);
      gl.deleteTexture(texture);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      // Deliberately NOT loseContext() here. This cleanup also runs when the
      // effect merely RE-RUNS against the same <canvas> (a renderEpoch bump, a
      // mode round-trip), and a canvas hands back the same context object on
      // every getContext('webgl') — including after it has been lost. Losing it
      // here left the next createWebGlResources compiling shaders on a dead
      // context: every compile fails, it returns null, failKnownGreenRender
      // hides both layers, and nothing but unmounting the element recovers.
      // Context disposal belongs to unmount alone; see the effect below.
      loseContextRef.current = () => gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [
    canvasEpoch,
    currentMedia,
    failKnownGreenRender,
    isLiveStream,
    mode,
    notifyReady,
    recoverCanvas,
    renderEpoch,
    sourceKey,
  ]);

  // Release the GPU context exactly once, when this surface goes away for good.
  // Keeping it out of the render effect's cleanup is what makes a re-run
  // survivable; keeping it here is what stops a long-lived page from leaking a
  // context per avatar surface it has mounted.
  useEffect(() => () => {
    loseContextRef.current?.();
    loseContextRef.current = null;
  }, []);

  if (!stream && !src && !useStaticImage) return null;

  const mediaStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit,
    pointerEvents: 'none',
    // Never flash the raw green source. The page/caller surface remains visible
    // during classification; native fallback appears only after bypass, while
    // a keyed source hands off atomically after the first GPU frame paints.
    opacity: mode === 'native' ? 1 : 0,
  };

  return (
    <span
      ref={wrapperRef}
      className={className}
      data-floe-avatar-surface="true"
      data-floe-avatar-keying={mode}
      aria-hidden="true"
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'block',
        overflow: 'hidden',
        background: 'transparent',
        contain: 'paint',
        ...style,
      }}
    >
      {useStaticImage ? (
        <img
          key="cors-image"
          ref={imageRef}
          // crossOrigin BEFORE src, on both elements: React writes attributes
          // in prop order, and the load must never be able to start as a
          // no-CORS request — that is the exact load that poisons the key.
          crossOrigin="anonymous"
          src={corsCacheKeyUrl(poster)}
          alt=""
          onLoad={handleMediaReady}
          onError={handleMediaError}
          style={mediaStyle}
        />
      ) : (
        <video
          key={stream ? 'stream-video' : 'cors-video'}
          ref={videoRef}
          // crossOrigin BEFORE src (see the <img> above).
          crossOrigin={stream ? undefined : 'anonymous'}
          src={stream || !src ? undefined : corsCacheKeyUrl(src)}
          // The PLAIN poster URL, on purpose. Every current caller with a
          // poster renders it through the layered wrapper's <img> instead, so
          // this attribute is idle today; if a future caller ever arms it,
          // a poster-frame fetch issued without CORS must land on the plain
          // key, never on the SDK's own one.
          poster={poster}
          autoPlay
          playsInline
          muted
          loop={!stream && loop}
          // A one-shot clip plays once, from a warm cache, the moment it is
          // asked for: let the browser take all of it. A loop streams.
          preload={!stream && !loop ? 'auto' : 'metadata'}
          onEnded={handleEnded}
          onLoadedMetadata={handleLoadedMetadata}
          onLoadedData={handleMediaReady}
          onCanPlay={handleMediaReady}
          onResize={reportDimensions}
          onError={handleMediaError}
          style={mediaStyle}
        />
      )}
      <canvas
        key={canvasEpoch}
        ref={canvasRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          display: 'block',
          objectFit,
          pointerEvents: 'none',
          opacity: mode === 'keyed' && canvasReady ? 1 : 0,
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
        }}
      />
    </span>
  );
};

/**
 * A keyed poster is the immediate visual layer while a static loop starts.
 * Keeping the poster and loop as independent surfaces prevents both failure
 * modes of a native `video poster`: exposing the raw green still, or hiding the
 * entire presenter until the first decoded video frame. The video takes over
 * only after its native/keyed surface is actually visible.
 *
 * Both layers stay MOUNTED, and the retired one sits at opacity 0 — so a resize
 * repaints a canvas nobody can see. That is deliberate and was priced: the
 * repaint is one draw call against GL resources that already exist, and the
 * poster layer has to come back correct if the video ever fails. Dirty-tracking
 * the hidden layer would buy a draw call and cost a class of bug where the
 * fallback surfaces at the wrong size.
 */
const LayeredPosterAvatarVideo: React.FC<AvatarVideoProps & { poster: string }> = ({
  stream,
  src,
  poster,
  loop,
  objectFit,
  className,
  style,
  onError,
  onReady,
  onEnded,
  onVideoError,
  playForMs,
  onDimensions,
  edgeBlur,
}) => {
  const [videoReady, setVideoReady] = useState(false);
  const posterFailedRef = useRef(false);
  const videoFailedRef = useRef(false);
  const videoDimensionsReportedRef = useRef(false);
  const readyNotifiedRef = useRef(false);
  const notifyReady = useCallback(() => {
    if (readyNotifiedRef.current) return;
    readyNotifiedRef.current = true;
    onReady?.();
  }, [onReady]);
  const reportPosterFailure = useCallback(() => {
    posterFailedRef.current = true;
    if (videoFailedRef.current) onError?.();
  }, [onError]);
  const reportVideoFailure = useCallback(() => {
    videoFailedRef.current = true;
    setVideoReady(false);
    if (posterFailedRef.current) onError?.();
  }, [onError]);
  const reportPosterDimensions = useCallback((dimensions: AvatarVideoDimensions) => {
    if (!videoDimensionsReportedRef.current) onDimensions?.(dimensions);
  }, [onDimensions]);
  const reportVideoDimensions = useCallback((dimensions: AvatarVideoDimensions) => {
    videoDimensionsReportedRef.current = true;
    onDimensions?.(dimensions);
  }, [onDimensions]);

  return (
    <span
      className={className}
      data-floe-avatar-surface="true"
      data-floe-avatar-layered="true"
      aria-hidden="true"
      style={{
        position: 'relative',
        display: 'block',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: 'transparent',
        contain: 'paint',
        ...style,
      }}
    >
      <span
        data-floe-avatar-poster-layer="true"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'block',
          opacity: videoReady ? 0 : 1,
          pointerEvents: 'none',
        }}
      >
        <AvatarVideoSurface
          poster={poster}
          reducedMotion
          objectFit={objectFit}
          edgeBlur={edgeBlur}
          onReady={notifyReady}
          onError={reportPosterFailure}
          onDimensions={reportPosterDimensions}
        />
      </span>
      <span
        data-floe-avatar-video-layer="true"
        style={{
          position: 'absolute',
          zIndex: 1,
          inset: 0,
          display: 'block',
          pointerEvents: 'none',
        }}
      >
        <AvatarVideoSurface
          stream={stream}
          src={src}
          loop={loop}
          objectFit={objectFit}
          edgeBlur={edgeBlur}
          onReady={() => {
            setVideoReady(true);
            notifyReady();
          }}
          onError={reportVideoFailure}
          onVideoError={onVideoError}
          onEnded={onEnded}
          playForMs={playForMs}
          onDimensions={reportVideoDimensions}
        />
      </span>
    </span>
  );
};

/**
 * Identity of the media a surface is rendering, used as its React key so ANY
 * source change remounts it against a fresh `<canvas>`.
 *
 * Load-bearing, not tidiness. Only the poster-layered branch used to carry a
 * key, so a site with an idle loop and NO poster kept one surface across the
 * idle-loop -> live-stream handover. That surface re-ran its render effect on
 * a canvas whose context the previous run had just lost, and stayed blank for
 * the rest of the session (see the cleanup comment in AvatarVideoSurface).
 * `reducedMotion` is part of the identity because it flips `useStaticImage`,
 * which changes which element the renderer reads from.
 */
export function avatarSourceKey(
  props: Pick<AvatarVideoProps, 'stream' | 'src' | 'poster' | 'reducedMotion'>,
): string {
  return [
    props.stream?.id ?? '',
    props.src ?? '',
    props.poster ?? '',
    props.reducedMotion ? 'reduced' : '',
  ].join('\u0000');
}

/** One source being rendered, plus the identity that keys its surface. */
interface AvatarLayer {
  key: string;
  props: AvatarVideoProps;
}

/** A source is worth bridging only if it can actually paint something. */
function hasRenderableSource(
  props: Pick<AvatarVideoProps, 'stream' | 'src' | 'poster'>,
): boolean {
  return !!(props.stream || props.src || props.poster);
}

/** The keyed surface for one source — the branch `AvatarVideo` used to be. */
function renderAvatarLayer(props: AvatarVideoProps, key: string): React.ReactElement {
  const { stream, src, poster, reducedMotion } = props;
  if ((stream || src) && poster && !reducedMotion) {
    return <LayeredPosterAvatarVideo key={key} {...props} poster={poster} />;
  }
  return <AvatarVideoSurface key={key} {...props} />;
}

export const AvatarVideo: React.FC<AvatarVideoProps> = (props) => {
  const sourceKey = avatarSourceKey(props);
  const livePropsRef = useRef(props);
  // `slot` names which of the two fixed slots holds the CURRENT source. A
  // handover flips it, so the source being retired stays in the slot it was
  // already rendered in and keeps its element — a moving layer would be a
  // remount, and remounting the outgoing <video> restarts the idle loop from
  // t=0 at the exact moment it is supposed to be covering the seam.
  const [bridge, setBridge] = useState<{
    outgoing: AvatarLayer | null;
    slot: 'a' | 'b';
    key: string;
  }>(() => ({ outgoing: null, slot: 'a', key: sourceKey }));
  const [incomingPainted, setIncomingPainted] = useState(true);
  const onReadyRef = useRef(props.onReady);
  onReadyRef.current = props.onReady;

  // Adjusting state DURING render — React's supported pattern for deriving
  // from props, and load-bearing here. Flipping the slot from an effect
  // instead would leave one committed render in which the new source occupies
  // the OLD slot, remounting both surfaces before the effect can correct it.
  // `livePropsRef` is refreshed in an effect, so it still holds the props of
  // the surface being replaced: that snapshot IS the outgoing layer, and there
  // is nowhere else to recover it from once the new props have rendered.
  if (bridge.key !== sourceKey) {
    const prior: AvatarLayer = { key: bridge.key, props: livePropsRef.current };
    // Reduced motion gets the hard cut it asks for, and there is nothing to
    // dissolve when either side of the swap paints nothing.
    const bridgeable =
      !props.reducedMotion &&
      hasRenderableSource(prior.props) &&
      hasRenderableSource(props);
    setBridge({
      outgoing: bridgeable ? prior : null,
      // Only a real handover flips slots. An unbridged change replaces the
      // source in place, which is the pre-existing keyed remount (FLO-754).
      slot: bridgeable ? (bridge.slot === 'a' ? 'b' : 'a') : bridge.slot,
      key: sourceKey,
    });
    setIncomingPainted(!bridgeable);
  }

  const { outgoing } = bridge;

  useEffect(() => {
    livePropsRef.current = props;
  });

  // Retire the outgoing layer once the fade has run — or once the cap expires,
  // for an incoming source that never reports a painted frame.
  useEffect(() => {
    if (!outgoing) return;
    const timer = window.setTimeout(
      () => {
        // Clearing only `outgoing` leaves `slot` alone, so the surviving source
        // never moves slots and is never remounted by the retirement itself.
        setBridge((prev) => (prev.outgoing ? { ...prev, outgoing: null } : prev));
        // Reaching here with `incomingPainted` still false means the CAP fired:
        // the incoming source never reported a frame. Reveal it regardless.
        // Dropping the outgoing layer while the incoming one is held at opacity
        // 0 would leave a permanently blank surface — worse than whatever the
        // incoming source is actually showing, which is exactly what a build
        // without this bridge would have displayed all along.
        setIncomingPainted(true);
      },
      incomingPainted ? AVATAR_CROSSFADE_MS : AVATAR_CROSSFADE_MAX_HOLD_MS,
    );
    return () => window.clearTimeout(timer);
  }, [outgoing, incomingPainted]);

  const handleIncomingReady = useCallback(() => {
    setIncomingPainted(true);
    onReadyRef.current?.();
  }, []);

  // Nothing to show and nothing to retire: render nothing at all, exactly as a
  // sourceless surface did before the bridge existed. An empty wrapper would
  // otherwise occupy its caller's box on surfaces that expect a hard absence.
  if (!hasRenderableSource(props) && !outgoing) return null;

  const fade = `opacity ${AVATAR_CROSSFADE_MS}ms ease`;
  const layerStyle = (role: 'current' | 'outgoing'): React.CSSProperties => ({
    position: 'absolute',
    inset: 0,
    display: 'block',
    pointerEvents: 'none',
    // The retiring source paints OVER the incoming one and dissolves to reveal
    // it, so a live surface that has not painted yet is never shown as a
    // transparent hole where the presenter was.
    zIndex: role === 'outgoing' ? 1 : 0,
    opacity: (role === 'current') === incomingPainted ? 1 : 0,
    transition: fade,
    // Keep a retired slot out of hit-testing and off the compositor entirely.
    visibility: role === 'outgoing' && !outgoing ? 'hidden' : 'visible',
  });

  const currentLayer = renderAvatarLayer(
    { ...props, className: undefined, style: undefined, onReady: handleIncomingReady },
    sourceKey,
  );
  const outgoingLayer = outgoing
    ? renderAvatarLayer(
        {
          ...outgoing.props,
          className: undefined,
          style: undefined,
          // The outgoing surface has already reported everything it is going to.
          // Re-firing these would tell the caller that a source it is no longer
          // showing just became ready, failed, or changed size.
          onReady: undefined,
          onError: undefined,
          onEnded: undefined,
          onVideoError: undefined,
          onDimensions: undefined,
        },
        outgoing.key,
      )
    : null;

  const renderSlot = (slot: 'a' | 'b') => {
    const isCurrent = bridge.slot === slot;
    return (
      <span
        key={slot}
        data-floe-avatar-current={isCurrent ? 'true' : undefined}
        style={layerStyle(isCurrent ? 'current' : 'outgoing')}
      >
        {isCurrent ? currentLayer : outgoingLayer}
      </span>
    );
  };

  // The wrapper is rendered unconditionally even when nothing is fading. It
  // fixes the incoming surface's position in the tree: React reconciles by
  // position as well as key, so mounting the bridge only during a fade would
  // move the live surface one level deeper and back, remounting it against a
  // fresh <canvas> at the exact moment the fade ends (FLO-754's blank-canvas
  // failure, re-introduced). One stable level costs nothing; a remount costs
  // the avatar.
  return (
    <span
      className={props.className}
      data-floe-avatar-crossfade="true"
      aria-hidden="true"
      style={{
        position: 'relative',
        display: 'block',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: 'transparent',
        ...props.style,
      }}
    >
      {/* Slot order in the DOM is FIXED. Which slot is current alternates, so
          select the live source by `[data-floe-avatar-current]`, never by
          position — position is what has to stay still for the retiring
          surface to keep its element. */}
      {renderSlot('a')}
      {renderSlot('b')}
    </span>
  );
};
